<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Button } from 'primevue';
import PackageDetailsPanel from './PackageDetailsPanel.vue';
import { usePackageDetailsStore } from '../stores/packageDetailsStore';
import { usePackageStore } from '../stores/packageStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';

const props = withDefaults(defineProps<{
  zIndex?: number;
}>(), {
  zIndex: 600,
});

const { translations } = storeToRefs(useLanguageStore());
const t = computed(() => translations.value.packages.details);

const detailsStore = usePackageDetailsStore();
const { packageId, isOpen, width, resizing } = storeToRefs(detailsStore);
const packageStore = usePackageStore();

// Keeps the content rendered while the drawer slides out.
const displayedId = ref<string | null>(packageId.value);
watch(packageId, (id) => {
  if (id) displayedId.value = id;
});

const title = computed(() => {
  const number = displayedId.value ? packageStore.getById(displayedId.value)?.number : null;
  return number ? `${t.value.title} ${number}` : t.value.title;
});

const drawer = useTemplateRef<HTMLElement>('drawer');

function onResizeStart(event: PointerEvent) {
  resizing.value = true;
  (event.target as HTMLElement).setPointerCapture(event.pointerId);
}

function onResizeMove(event: PointerEvent) {
  if (!resizing.value || !drawer.value) return;
  const right = drawer.value.getBoundingClientRect().right;
  width.value = detailsStore.clampWidth(right - event.clientX);
}

function onResizeEnd(event: PointerEvent) {
  if (!resizing.value) return;
  resizing.value = false;
  (event.target as HTMLElement).releasePointerCapture(event.pointerId);
}

function isInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !isOpen.value) return;
  if (event.defaultPrevented || isInputTarget(event.target)) return;
  detailsStore.close();
}

onMounted(() => window.addEventListener('keydown', onKeyDown));
onBeforeUnmount(() => window.removeEventListener('keydown', onKeyDown));
</script>

<template>
  <aside ref="drawer" class="package-details-drawer" :class="{ open: isOpen, resizing }"
    :style="{ width: `${width}px`, zIndex: props.zIndex }" :aria-hidden="!isOpen">
    <div class="drawer-resize-handle" :title="t.resize" @pointerdown="onResizeStart" @pointermove="onResizeMove"
      @pointerup="onResizeEnd" @pointercancel="onResizeEnd"></div>
    <header class="drawer-header">
      <span class="drawer-title">{{ title }}</span>
      <Button icon="pi pi-times" text rounded severity="secondary" :aria-label="translations.generic.close"
        @click="detailsStore.close()"></Button>
    </header>
    <div class="drawer-body">
      <PackageDetailsPanel v-if="displayedId" :package-id="displayedId"></PackageDetailsPanel>
    </div>
  </aside>
</template>

<style scoped>
/* Overlay drawer on the right; slides over the map so the map never resizes. */
.package-details-drawer {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--p-content-background);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.25);
  transform: translateX(100%);
  visibility: hidden;
  transition: transform 0.25s ease, visibility 0s linear 0.25s;
}

.package-details-drawer.open {
  transform: translateX(0);
  visibility: visible;
  transition: transform 0.25s ease;
}

.package-details-drawer.resizing {
  transition: none;
  user-select: none;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.drawer-title {
  font-size: 1.125rem;
  font-weight: 600;
}

.drawer-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.drawer-resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  touch-action: none;
  z-index: 1;
}

.drawer-resize-handle:hover {
  background: var(--p-primary-color);
  opacity: 0.4;
}
</style>
